import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import { PagePaths } from '../../../utils/PagePaths'
import { Props } from './Layout'
import { ArcadeMachineDetailDocument, ArcadeMachineDetailQuery } from './query.generated'
import { useApolloClient } from '@apollo/client'
import { useGameInfo } from '@/src/stores/Games/useGames'
import { S3URLService } from '@/src/services/S3URLService'

export type Params = {
  arcadeMachineId: string
}

export const useProps = ({ arcadeMachineId }: Params): Props => {
  const router = useRouter()
  const cilent = useApolloClient()

  const [viewType, setViewType] = useState<Props['viewType']>(null)
  const [data, setData] = useState<ArcadeMachineDetailQuery | null>(null)
  const [loading, setLoading] = useState<Props['loading']>(true)
  const [visibleHelp, setVisibleHelp] = useState(false)
  const [visibleInOperationCheckModal, setVisibleInOperationCheckModal] = useState(false)

  const fetchArcadeDetail = async () => {
    return cilent.query({
      query: ArcadeMachineDetailDocument,
      variables: { where: { id: arcadeMachineId } },
    })
  }

  const initLoad = async () => {
    setLoading(true)
    const newData = await fetchArcadeDetail()
    setData(newData.data)
    setViewType(newData.data?.arcadeMachine?.playing ? 'IN_OPERATION' : 'READY')
    setLoading(false)
  }

  // 表示に変更があった場合、画面表示更新
  const reloadDisplay = async (isLoading = false) => {
    if (isLoading) setLoading(true)
    const newData = await fetchArcadeDetail()
    if (
      !data ||
      data.arcadeMachine?.energy !== newData.data?.arcadeMachine?.energy ||
      data.arcadeMachine?.extractedEnergy !== newData.data?.arcadeMachine?.extractedEnergy ||
      data.arcadeMachine?.megaSparkUpcoming !== newData.data?.arcadeMachine?.megaSparkUpcoming ||
      data.arcadeMachine?.playing !== newData.data?.arcadeMachine?.playing
    ) {
      if (!!!newData.data?.arcadeMachine?.playing) {
        setViewType('IN_OPERATION_PLAY')
      }
      setData(newData.data)
    }
    if (isLoading) setLoading(false)
  }

  const { games } = useGameInfo()
  const gameDetail = games.find((e) => e.id === data?.arcadeMachine?.game ?? '')
  const onClickPlay: Props['onClickPlay'] = useCallback(async () => {
    const newData = await fetchArcadeDetail()
    if (!!newData?.data?.arcadeMachine?.playing) {
      setVisibleInOperationCheckModal(true)
    } else {
      router.push(PagePaths.arcadeGamePlay({ arcadeMachineId }))
    }
  }, [router, arcadeMachineId, fetchArcadeDetail])

  const initInterval = () => {
    const interval = setInterval(async () => {
      // 10秒に一度最新情報取得して状態確認
      await reloadDisplay()
    }, 10000)
    return interval
  }

  useEffect(() => {
    initLoad()
  }, [])

  useEffect(() => {
    if (!viewType || viewType === 'READY') return
    const interval = initInterval()
    return () => clearInterval(interval)
  }, [viewType])

  return {
    viewType,
    loading,
    teras: data?.arcadeMachine?.rewardForSparking ? Number(data?.arcadeMachine?.rewardForSparking) : 0,
    arcadeImage: data?.arcadeMachine?.metadata.withoutAccImageUrl ?? '',
    title: gameDetail?.name ?? '',
    imageUrl: gameDetail ? S3URLService.getGameThumbnailImageURL(gameDetail?.id) : '',
    movie: gameDetail ? S3URLService.getGamePlayMovieURL(gameDetail?.id) : '',
    rule: gameDetail?.rule ?? '',
    category: gameDetail?.category ?? '',
    energy: data?.arcadeMachine?.energy ?? 0,
    maxEnergy: data?.arcadeMachine?.maxEnergy ?? 0,
    extractedEnergy: data?.arcadeMachine?.extractedEnergy ?? 0,
    megaSparkUpcoming: !!data?.arcadeMachine?.megaSparkUpcoming,
    feverSparkRemain: data?.arcadeMachine?.feverSparkRemain ?? null,
    onClickPlay,
    onClickHelp: () => {
      setVisibleHelp((t) => !t)
    },
    onClickHelpClose: () => {
      setVisibleHelp(false)
    },
    visibleHelp,
    helpDescription: gameDetail?.help.description ?? '',
    helpHowTo: gameDetail?.help.howTo ?? '',
    machineImage: gameDetail ? S3URLService.getGameBackgroundImageURL(gameDetail?.id) : '',
    machineImageInOperation: gameDetail ? S3URLService.getGameInOperationImageURL(gameDetail?.id) : '',
    visibleInOperationCheckModal,
    onClickWait: async () => {
      setVisibleInOperationCheckModal(false)
      await reloadDisplay(true)
      setViewType('IN_OPERATION')
    },
    onClickSearch: () => {
      setVisibleInOperationCheckModal(false)
      router.push(PagePaths.arcadeMachineSearch(data?.arcadeMachine?.game ?? ''))
    },
  }
}
